/**
 * React Query hooks for Entra ID group search and creation.
 * Used by the TierGroupPicker in settings.
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";

export interface EntraGroup {
  readonly id: string;
  readonly displayName: string;
  readonly securityEnabled: boolean;
  readonly isAssignableToRole: boolean;
}

interface SearchResult {
  readonly items: EntraGroup[];
}

export function useEntraGroupSearch(query: string) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["entra-groups", "search", query],
    queryFn: () =>
      api.get<SearchResult>(
        `/api/admin/entra-groups/search?q=${encodeURIComponent(query)}`,
      ),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

export function useEntraGroup(id: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["entra-groups", id],
    queryFn: () => api.get<EntraGroup>(`/api/admin/entra-groups/${id}`),
    enabled: !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
    staleTime: 300_000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}

export function useCreateEntraGroup() {
  const api = useApiClient();

  return useMutation({
    mutationFn: (tier: string) =>
      api.post<EntraGroup>("/api/admin/entra-groups", { tier }),
  });
}
